import { Box, Button, Typography, styled } from '@mui/material'
import { selectCurrentInvoice } from 'app/slice/invoice.slice'
import BtnEditUnderline from 'assets/button/BtnEditUnderline'
import { Config } from 'config'
import { logo } from 'constants/images'
import moment from 'moment'
import PageModal from 'new-components/modal/page-modal'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getClinicInfo, numberFormat } from 'utils/app.utils'
import { dateMonthToView, dateToView } from 'utils/date.utils'
import { getInvoiceLineItems, getUnderlineLayout, readLineHeight, UnderlineLayout } from './pdfInvoice.utils'

// type Props = {}

export const PDFContainer = styled(Box)(({ theme }) => ({
    width: '297mm',
    height: '420mm',
    backgroundColor: "#fff",
    overflow: "hidden",
    position: 'relative',
    '.pdf-body': {
        padding: '20px 60px',
        height: '100%',
        width: "100%",
        position: 'relative',
        '.pdf-title': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: '24px',
            '.pdf-branch-logo': {
                height: '100px',
                img: {
                    maxHeight: '100%'
                }
            },
            '.pdf-title-text': {
                fontWeight: 'bolder',
                color: 'blue'
            }
        },
        '.pdf-content': {
            '.pdf-content-date': {
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'relative'
            },
            '.pdf-table-container': {
                marginTop: '8px',
                'table': {
                    width: '100%'
                },
                'table .table-head th': {
                    border: '1px solid blue',
                    textAlign: 'center',
                    padding: '8px 8px ',
                },
                'table .table-body td': {
                    border: '1px solid blue',
                    padding: '8px',
                    height: '24px',
                    minHeight: '19px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                }
            },
            '.pdf-text-detail': {
                display: 'flex',
                marginTop: "24px",
                '.pdf-text-detail-value': {
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'calc(100% - 150px)',
                    '.pdf-value-text': {
                        position: 'relative',
                        width: '100%'

                    }
                }
            },
        },
        '.pdf-container-signature': {
            marginTop: 'auto',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: ' center',
            width: '100%'
        }
    },
    '.text-break-word': {
        wordWrap: 'break-word',
        whiteSpace: 'normal',
        overflowWrap: 'break-word'
    },

    '&.pdf-invoice-tdh': {
        boxSizing: 'border-box',
        fontSize: '14px',
        lineHeight: 1.4,
        width: '210mm',
        minHeight: '297mm',
        height: 'auto',
        overflow: 'visible',
        '.pdf-body': {
            boxSizing: 'border-box',
            position: 'relative',
            top: 'auto',
            minHeight: 'calc(297mm - 40px)',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
        },
        '.pdf-content': {
            flex: '0 0 auto',
        },
        '.pdf-table-container': {
            table: {
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                breakInside: 'auto',
                pageBreakInside: 'auto',
            },
            thead: {
                display: 'table-header-group',
            },
            'tbody tr': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
            },
            'table .table-body td': {
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                overflow: 'visible',
            },
            'tbody tr:nth-last-of-type(2)': {
                breakAfter: 'avoid',
                pageBreakAfter: 'avoid',
            },
        },
        '.pdf-invoice-footer': {
            position: 'static',
            width: '100%',
            marginTop: 'auto',
            paddingTop: '24px',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
        },
        '@media print': {
            width: '210mm',
            minWidth: '210mm',
            minHeight: 0,
            height: 'auto',
            margin: '0 auto',
            fontSize: '12px',
            lineHeight: 1.25,
            '.pdf-body': {
                minHeight: '277mm',
                padding: '10mm',
                display: 'flex',
                flexDirection: 'column',
            },
            '.pdf-invoice-footer': {
                marginTop: 'auto !important',
                paddingTop: '0 !important',
                '.pdf-signature-row': {
                    margin: '0 !important',
                    alignItems: 'flex-start',
                },
                '.pdf-signature-column': {
                    width: '48%',
                },
                '.pdf-signature-image': {
                    marginTop: '0 !important',
                    marginBottom: '0 !important',
                },
                '.signature-button': {
                    width: '170px !important',
                    height: '55px !important',
                    minHeight: '55px !important',
                    maxHeight: '55px !important',
                    padding: '0 !important',
                    gap: '2px',
                },
                '.signature-button img': {
                    width: '90px !important',
                    height: '55px !important',
                },
                '.pdf-signature-labels .MuiTypography-root': {
                    fontSize: '8px !important',
                    lineHeight: '1.05 !important',
                    marginTop: '1px !important',
                    marginBottom: '1px !important',
                },
                '.pdf-branch-contact': {
                    marginTop: '4px !important',
                    alignItems: 'flex-start',
                    '.pdf-branch-name, .pdf-branch-address, .pdf-branch-phone': {
                        fontSize: '8px !important',
                        lineHeight: '1.05 !important',
                    },
                    'img': {
                        height: '45px !important',
                        width: 'auto !important',
                    },
                },
            },
        },
    },

}))

// Legacy (non-TDH) invoice lines are 24px tall; the TDH layout overrides font-size/line-height.
const DEFAULT_LINE_HEIGHT = 24
const DEFAULT_UNDERLINE_LAYOUT: UnderlineLayout = { count: 1, lineHeight: DEFAULT_LINE_HEIGHT }
const measureUnderlines = (element: HTMLElement): UnderlineLayout =>
    getUnderlineLayout(element.clientHeight, readLineHeight(element, DEFAULT_LINE_HEIGHT))

const PDFLineText = styled(Box)(({ theme }) => ({
    position: 'absolute',
    width: '150px',
    borderBottom: '1px dashed black'
}))

const StyledSignatureButton = styled(Button)`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 300px;
  height: 138px;
  border: 1px dashed #e5e5e5;
  padding: 10px 0px;
  color: #808080;
  background-color: transparent;
  &:hover {
    border-color: #808080;
    background-color: #f5f5f5;
  }
  @media print {
     '.signature-button': {
        border: 'none !important',
    },
  }
`;

// Address branches data is now loaded from public/address-branches.js
// This allows updating branch information without rebuilding the application
const Address_branches = (window as any).ADDRESS_BRANCHES || {};

type PdfInvoiceProps = {
    type: 'treatment' | 'historyTreatment' | 'payment'
    docterId: any
    noteNextVisit: any
    treatment: any
    childrenModal?: ReactNode
    openModal: (type: 'doctor' | 'patient') => void
    clear?: boolean
    disabledBtn?: boolean
    submit?: boolean
    onSubmit: (e: boolean) => void
}

// Lao translations mapping for VDH domain
const laoTranslations: Record<string, string> = {
    'ใบแจ้งค่าบริการรักษา': 'ໃບແຈ້ງຄ່າບໍລິການປິ່ນປົວ',
    'วันที่': 'ວັນທີ',
    'ชื่อ-นามสกุล': 'ຊື່-ນາມສະກຸນ',
    'รหัสผู้ป่วย': 'ລະຫັດຜູ້ປ່ວຍ',
    'ลำดับ': 'ລຳດັບ',
    'รหัส': 'ລະຫັດ',
    'รายการรักษา': 'ລາຍການປິ່ນປົວ',
    'จำนวน': 'ຈຳນວນ',
    'ราคา(บาท)': 'ລາຄາ(ກີບ)',
    'ราคารวม': 'ລາຄາລວມ',
    'หมายเหตุ': 'ຫມາຍເຫດ',
    'นัดหมายครั้งต่อไป': 'ນັດຫມາຍຄັ້ງຕໍ່ໄປ',
    'ทันตแพทย์ผู้ให้การรักษา': 'ທັນຕະແພດຜູ້ໃຫ້ການປິ່ນປົວ',
    'ผู้รับบริการรักษา': 'ຜູ້ຮັບບໍລິການປິ່ນປົວ',
    'ลายเซ็น': 'ລາຍເຊັນ',
    'คลิกเพื่อเขียนลายเซ็น': 'ກົດເພື່ອເຂົ້າສູ່ລະບົບ',
    'บริการ': 'ບໍລິການ'
}

const PdfInvoice = (props: PdfInvoiceProps) => {
    const { treatment } = props
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const currentInvoice = useSelector(selectCurrentInvoice);
    const [textRemark, setTextRemark] = useState<string[]>([''])
    const [lineRemark, setLineRemark] = useState<UnderlineLayout>(DEFAULT_UNDERLINE_LAYOUT)
    const [textNextVisit, setTextNextVisit] = useState<string[]>([''])
    const [noteNextVisit, setNoteNextVisit] = useState<string>('')
    const [date, setDate] = useState<string>('')
    const [lineNextVisit, setLineNextVisit] = useState<UnderlineLayout>(DEFAULT_UNDERLINE_LAYOUT)
    const [totalTreatment, setTotalTreatment] = useState<number>(0)
    const [isOpenPageModal, setIsOpenPageModal] = useState(false)


    const textRemarkRef = useRef<any>(null)
    const textNextVisitRef = useRef<any>(null)

    const BranchInfo = getClinicInfo()
    const [branchAddress, setBranchAddress] = useState<any>()

    // Check if domain is VDH
    const isVDHDomain = Config.IS_VDH === '1' || false

    // Helper function to get translation (Thai or Lao based on domain)
    const getTranslation = (key: string): string => {
        const thaiText = t(key)
        if (isVDHDomain && laoTranslations[thaiText]) {
            return laoTranslations[thaiText]
        }
        return thaiText
    }




    const headCells = [
        { title: getTranslation('PDF_INVOICE.PDF.NO'), title_en: t('PDF_INVOICE.PDF.NO_EN'), style: { width: '70px' } },
        { title: getTranslation('PDF_INVOICE.PDF.CODE'), title_en: t('PDF_INVOICE.PDF.CODE_EN'), style: { width: '70px' } },
        { title: getTranslation('PDF_INVOICE.PDF.TREATMENT_LIST'), title_en: t('PDF_INVOICE.PDF.TREATMENT_LIST_EN'), style: { width: 'auto' } },
        { title: getTranslation('PDF_INVOICE.PDF.QUANTITY'), title_en: t('PDF_INVOICE.PDF.QUANTITY_EN'), style: { width: '100px' } },
        { title: getTranslation('PDF_INVOICE.PDF.PRICE'), title_en: t('PDF_INVOICE.PDF.PRICE_EN'), style: { width: '120px' } },
    ]



    useEffect(() => {
        renderTextDetail()
        handleTotalTreatment()
        setDate(dateMonthToView(treatment?.patientInfo?.visitDate || new Date(), true))
        setBranchAddress(Address_branches?.[treatment?.branch?.branchId])
    }, [treatment])

    const handleTotalTreatment = () => {
        const newTotalPD = treatment?.summeryProducts?.map((item: any) => item?.total || 0)
            .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
        const newTotal = treatment?.summeryTreatments?.map((treatment: any) => treatment?.total || 0)
            .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
        setTotalTreatment((newTotal + newTotalPD) || 0)
    }

    const invoiceLineItems = getInvoiceLineItems(treatment)

    const handlePatientCode = () => {
        if (BranchInfo?.patientCodeAppointment === '1') {
            return treatment?.patientInfo?.cnNumber || '-'
        } else if (BranchInfo?.patientCodeAppointment === '0') {
            return treatment?.patientInfo?.branchCnNumber || '-'
        } else if (BranchInfo?.patientCodeAppointment === '2') {
            return treatment?.patientInfo?.snCode || '-'
        } else {
            return '-'
        }
    }

    const renderTextDetail = useCallback(() => {

        const handleTreatment = (data: string) => {
            const dataTreatment = JSON.parse(data)
            const textTreatment: string[] = []
            dataTreatment?.forEach((item: any) => {
                textTreatment.push(item?.operativeName || item?.operativeTypeName)
            })
            return textTreatment.toString()
        }

        const newTextRemark: string[] = []
        const newTextNextVisit: string[] = []
        treatment?.summeryTreatments?.forEach((item: any) => {
            const dataNV = item?.nextVisit
            newTextRemark.push(item.note)
            if (dataNV?.date) {
                // thai or lao based on domain
                const dateLabel = isVDHDomain ? 'ວັນທີ' : 'วันที่'
                const serviceLabel = isVDHDomain ? 'ບໍລິການ' : 'บริการ'
                newTextNextVisit.push(`${dateLabel}: ${dateToView(dataNV?.date)} ${moment(dataNV?.timeStart, 'HH:mm:ss').format('HH:mm')} ${dataNV?.prefix || ''} ${dataNV?.doctorFullname} ${serviceLabel}: ${handleTreatment(dataNV?.treatment)}`)
            }

        })

        setNoteNextVisit(
            props.noteNextVisit
                .filter((e: any) => e.doctorId === treatment.doctorId)
                .map((e: any) => e.noteNextVisit)
        );
        setTextRemark(newTextRemark)
        setTextNextVisit(newTextNextVisit)
    }, [treatment])

    useEffect(() => {
        if (textRemarkRef?.current) {
            setLineRemark(measureUnderlines(textRemarkRef.current))
        }
    }, [textRemarkRef?.current?.clientHeight, textRemark])

    useEffect(() => {
        if (textNextVisitRef?.current) {
            setLineNextVisit(measureUnderlines(textNextVisitRef.current))
        }
    }, [textNextVisitRef?.current?.clientHeight, textNextVisit])

    const onTableEmpty = useCallback(() => {
        const numberTotal = (treatment?.summeryTreatments?.length || 0) + (treatment?.summeryProducts?.length || 0)
        const numberTB = numberTotal >= 13 ? 0 : 13 - numberTotal
        return numberTB
    }, [treatment])

    const SignatureButton = ({ type, children, disabled, className }: { type: "doctor" | "patient", children: ReactNode, disabled?: boolean, className?: string; }) => {
        return (
            <StyledSignatureButton
                id="signature-button"
                className={`${className} signature-button`}
                disabled={disabled ?? false}
                variant="outlined"
                onClick={() => {
                    props.openModal(type);
                    props.onSubmit(false);
                    setIsOpenPageModal(true);
                }}
            >{children}</StyledSignatureButton>
        );
    };

    const handleSignature = (type: "submit" | "clear") => {
        type === 'submit' ? props.onSubmit(true) : props.onSubmit(false)
        setIsOpenPageModal(false);
    };

    return (
        <>
            <PDFContainer className={Config.IS_TDH === '1' ? 'pdf-invoice-tdh' : undefined}>
                {Config.IS_TDH === '1' && (<Box style={{ position: 'absolute', top: '50px', right: '50px' }}>
                    <Box sx={{
                        height: '50px',
                        width: '200px',
                        border: '1px solid black',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}><Typography>{branchAddress?.name}</Typography>
                    </Box>
                </Box>)}
                <Box className={'pdf-body'} style={Config.IS_TDH === '1' ? undefined : { position: 'absolute', top: '0px' }}>
                    <Box display="flex" justifyContent="center" >
                        <Box className={'pdf-title'} >
                            <Box className={'pdf-branch-logo'} ><img src={BranchInfo?.branchLogo || logo.dentalLogoHorizontal} onError={(event) => {
                                event.currentTarget.onerror = null
                                event.currentTarget.src = logo.dentalLogoHorizontal
                            }} alt="clinic logo" /></Box>
                            <Box className={'pdf-title-text'} sx={{ fontSize: '20px' }}>{getTranslation("PDF_INVOICE.TITLE")}</Box>
                            <Box className={'pdf-title-text'} sx={{ fontSize: '18px' }}>{t("PDF_INVOICE.TITLE_EN")}</Box>
                        </Box>
                    </Box>
                    <Box className={'pdf-content'}>
                        <Box className={'pdf-content-date'} >
                            <span style={{ fontWeight: '500' }}>{getTranslation("PDF_INVOICE.PDF.DATE")}/{t("Date")}</span>
                            <span style={{ width: '200px', display: 'flex', justifyContent: 'flex-end' }}>&nbsp;&nbsp;{date}</span>
                            <PDFLineText sx={{ bottom: '0', }} />
                        </Box>
                        <Box sx={{ display: 'flex', marginTop: '8px' }}>
                            <Box sx={{ width: '60%' }}>
                                <Box sx={{ fontWeight: '500', }}>
                                    <span>{getTranslation('PDF_INVOICE.PDF.PATIENT_NAME')}/{t('PDF_INVOICE.PDF.SUB_PATIENT_NAME')}</span><span>&nbsp;&nbsp;{`${treatment?.patientInfo?.firstname} ${treatment?.patientInfo?.lastname}`}</span>
                                </Box>
                            </Box>
                            <Box>
                                <Box sx={{ fontWeight: '500', }}>
                                    <span>{getTranslation('PDF_INVOICE.PDF.PATIENT_ID')}/{t('PDF_INVOICE.PDF.SUB_PATIENT_ID', { patientCodeQueue: BranchInfo?.patientCodeQueue === '0' ? 'PT' : BranchInfo?.patientCodeQueue === '1' ? 'CN' : 'SN' })}</span><span>&nbsp;&nbsp;{handlePatientCode()}</span>
                                </Box>
                            </Box>
                        </Box>

                        <Box className={'pdf-table-container'}>
                            <table>
                                <thead>
                                <tr className={'table-head'}>
                                    {headCells.map((item, index: number) => {
                                        return (
                                            <th key={index} style={{ ...item?.style }}>
                                                <Box>{item.title}</Box>
                                                <Box>{item.title_en}</Box>
                                            </th>
                                        )
                                    })}
                                </tr>
                                </thead>
                                <tbody>
                                {Config.IS_TDH === '1' ? invoiceLineItems.map((item) => (
                                    <tr className={'table-body'} key={`${item.number}-${item.code}`}>
                                        <td style={{ textAlign: 'center', maxWidth: '70px' }}>{item.number}</td>
                                        <td style={{ textAlign: 'center', maxWidth: '120px' }}>{item.code}</td>
                                        <td style={{ maxWidth: '600px' }}>{item.description}</td>
                                        <td style={{ textAlign: 'center', maxWidth: '100px' }} >{item.quantity}</td>
                                        <td style={{ textAlign: 'right', maxWidth: '120px' }}>{numberFormat(item.total)}</td>
                                    </tr>
                                )) : (
                                    <>
                                        {treatment?.summeryTreatments && treatment?.summeryTreatments?.map((item: any, index: number) => {
                                            if (index <= 12) {
                                                return (
                                                    <tr className={'table-body'} key={index}>
                                                        <td style={{ textAlign: 'center', maxWidth: '70px' }}>{index + 1}</td>
                                                        <td style={{ textAlign: 'center', maxWidth: '120px' }}>{item?.operativeCode || ''}</td>
                                                        <td style={{ maxWidth: '600px' }}>{item?.operativeName}</td>
                                                        <td style={{ textAlign: 'center', maxWidth: '100px' }} >{item?.qty || 0}</td>
                                                        <td style={{ textAlign: 'right', maxWidth: '120px' }}>{numberFormat(item?.total || 0)}</td>
                                                    </tr>
                                                )
                                            }
                                        })}
                                        {treatment?.summeryProducts && treatment?.summeryProducts?.map((item: any, index: number) => {
                                            if (((treatment?.summeryTreatments?.length || 0) + index) <= 12) {
                                                return (
                                                    <tr className={'table-body'} key={index}>
                                                        <td style={{ textAlign: 'center', maxWidth: '70px' }}>{index + 1 + (treatment?.summeryTreatments?.length || 0)}</td>
                                                        <td style={{ textAlign: 'center', maxWidth: '120px' }}>{item?.itemCode || ''}</td>
                                                        <td style={{ maxWidth: '600px' }}>{item?.itemName || ''}</td>
                                                        <td style={{ textAlign: 'center', maxWidth: '100px' }} >{item?.qty || 0}</td>
                                                        <td style={{ textAlign: 'right', maxWidth: '120px' }}>{numberFormat(item?.total || 0)}</td>
                                                    </tr>
                                                )
                                            }
                                        })}
                                        {new Array(onTableEmpty())?.fill(undefined)?.map((item, index: number) => (
                                            <tr className={'table-body'} key={`empty-${index}`}>
                                                {new Array(5).fill(undefined).map((info, num: number) => (
                                                    <td key={num} >
                                                        <Box sx={{ height: '24px', }}>
                                                        </Box>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </>
                                )}
                                <tr style={{ fontWeight: '500' }}>
                                    <td colSpan={3} >
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '8px 8px ', border: '1px solid blue', }} >
                                        <Box sx={{ minHeight: '19px' }}>
                                            <Box>
                                                {getTranslation('PDF_INVOICE.PDF.TOTAL')}
                                            </Box>
                                            <Box>
                                                {t('PDF_INVOICE.PDF.TOTAL_EN')}
                                            </Box>
                                        </Box>
                                    </td>
                                    <td style={{ border: '1px solid blue', padding: '8px', textAlign: 'right', }}>
                                        <Box sx={{ minHeight: '19px' }}>
                                            {numberFormat(totalTreatment)}
                                        </Box>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </Box>

                        <Box className={'pdf-text-detail'}>
                            <Box>
                                <Box sx={{ fontWeight: '500', width: '150px' }}>{getTranslation('PDF_INVOICE.PDF.REMARK')}/ {t('PDF_INVOICE.PDF.REMARK_EN')} :</Box>
                            </Box>
                            <Box className={'pdf-text-detail-value'}>
                                <Box className={'pdf-value-text pdf-text-remark text-break-word'} ref={textRemarkRef} >
                                    {textRemark.filter((item) => item !== '').join('; ')}
                                    {new Array(lineRemark.count).fill(undefined).map((item: any, index: any) =>
                                        <PDFLineText
                                            key={index}
                                            sx={{
                                                top: `${Math.round(lineRemark.lineHeight * (index + 1)) - 1}px`,
                                                width: '100% !important',
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Box className={'pdf-text-detail'} sx={{ marginTop: "40px !important" }}>
                            <Box><Box sx={{ fontWeight: '500', whiteSpace: 'nowrap', paddingRight: '8px' }}>{getTranslation('PDF_INVOICE.PDF.NEXT_VISIT')}/{t('PDF_INVOICE.PDF.NEXT_VISIT_EN')} :</Box></Box>
                            <Box className={'pdf-text-detail-value'} sx={{ width: 'auto !important', flex: 1 }}>
                                <Box className={'pdf-value-text pdf-text-next-visit text-break-word'} ref={textNextVisitRef} >
                                    {noteNextVisit}
                                    {new Array(lineNextVisit.count).fill(undefined).map((item: any, index: any) =>
                                        <PDFLineText
                                            key={index}
                                            sx={{
                                                top: `${Math.round(lineNextVisit.lineHeight * (index + 1)) - 1}px`,
                                                width: '100% !important',
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        className="pdf-invoice-footer"
                        sx={Config.IS_TDH === '1' ? {
                            position: 'static',
                            width: '100%',
                            marginTop: 'auto',
                            paddingTop: '24px',
                        } : {
                            position: 'absolute',
                            bottom: '70px',
                            width: "calc(100% - 120px)",
                        }}
                    >
                        <Box className="pdf-signature-row" display="flex" justifyContent="space-between">
                            <Box className="pdf-signature-column" sx={{ textAlign: 'center' }}>
                                {currentInvoice?.doctorSignature ?
                                    <Box className="pdf-signature-image" marginY='10px'>
                                        <SignatureButton type='doctor' disabled={(treatment?.invoice?.doctorSignature && treatment?.invoice?.patientSignature)}>
                                            <img src={`data:image/png;base64,${currentInvoice?.doctorSignature}`} style={{ width: '178px', height: '130px' }} />
                                        </SignatureButton>
                                    </Box> : <Box marginBottom={'20px'}>
                                        <SignatureButton type='doctor' disabled={(treatment?.invoice?.doctorSignature && treatment?.invoice?.patientSignature)}>
                                            <BtnEditUnderline color={'#808080'} width='35' height='35' />
                                            <Typography sx={{ fontWeight: '500', fontSize: '20px' }}>{getTranslation(`PDF_INVOICE.PDF.CLICK_SIGNATURE`)}</Typography>
                                        </SignatureButton>
                                    </Box>}
                                <Box className="pdf-signature-labels">
                                    <Box sx={{ borderBottom: "1px dashed black", width: "200px", margin: "0 auto" }} />
                                    <Typography
                                        sx={{
                                            fontWeight: "500",
                                            fontSize: 14,
                                            textDecoration: "underline",
                                            textDecorationStyle: "dotted",
                                            textDecorationColor: "#000",
                                            marginBottom: '8px',
                                            marginTop: '10px'
                                        }}
                                    >{`( ${treatment?.firstname} ${treatment?.lastname} )`}
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, marginBottom: '8px' }}>{getTranslation('PDF_INVOICE.PDF.DOCTOR_SIGNATURE')}</Typography>
                                    <Typography sx={{ fontSize: 14, marginBottom: '8px' }}>{t('PDF_INVOICE.PDF.DOCTOR_SIGNATURE_EN')}</Typography>
                                </Box>
                            </Box>
                            <Box className="pdf-signature-column" sx={{ textAlign: 'center' }}>
                                {currentInvoice?.patientSignature ?
                                    <Box className="pdf-signature-image" marginY='10px'>
                                        <SignatureButton type='patient' disabled={treatment?.invoice?.doctorSignature && treatment?.invoice?.patientSignature}>
                                            <img src={`data:image/png;base64,${currentInvoice?.patientSignature}`} style={{ width: '178px', height: '130px' }} />
                                        </SignatureButton>
                                    </Box>
                                    : <Box marginBottom={'20px'}>
                                        <SignatureButton type='patient' disabled={treatment?.invoice?.doctorSignature && treatment?.invoice?.patientSignature}>
                                            <BtnEditUnderline color={'#808080'} width='35' height='35' />
                                            <Typography sx={{ fontWeight: '500', fontSize: '20px' }}>{getTranslation(`PDF_INVOICE.PDF.CLICK_SIGNATURE`)}</Typography>
                                        </SignatureButton>
                                    </Box>}
                                <Box className="pdf-signature-labels">
                                    <Box sx={{ borderBottom: "1px dashed black", width: "200px", margin: "0 auto" }} />
                                    <Typography sx={{
                                        fontWeight: "500",
                                        fontSize: 14,
                                        textDecoration: "underline",
                                        textDecorationStyle: "dotted",
                                        textDecorationColor: "#000",
                                        marginBottom: '8px',
                                        marginTop: '10px'
                                    }}>{`( ${treatment?.patientInfo?.firstname} ${treatment?.patientInfo?.lastname} )`}</Typography>
                                    <Typography sx={{ fontSize: 14, marginBottom: '8px' }}>{getTranslation('PDF_INVOICE.PDF.PATIENT_SIGNATURE')}</Typography>
                                    <Typography sx={{ fontSize: 14, marginBottom: '8px' }}>{t('PDF_INVOICE.PDF.PATIENT_SIGNATURE_EN')}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {Config.IS_TDH === '1' ?
                            <Box className="pdf-branch-contact" display="flex" justifyContent="space-between" alignItems="center" sx={{ marginTop: 0 }}>
                                <Box sx={{ width: "20%", textAlign: "left" }}></Box>
                                <Box sx={{ width: "60%", textAlign: "center", marginTop: "20px" }}>
                                    {branchAddress?.company && (
                                        <Box className="pdf-branch-name" sx={{ fontWeight: "600", }}>{`${branchAddress?.company}`}</Box>
                                    )}
                                    {branchAddress?.address && (
                                        <Box className="pdf-branch-address">{`${branchAddress?.address}`}</Box>
                                    )}
                                    {(branchAddress?.contact?.T || branchAddress?.contact?.M) && (
                                        <Box className="pdf-branch-phone">
                                            {branchAddress?.contact?.T && (
                                                <>
                                                    <span style={{ fontWeight: "600" }}>{`T`}</span> {`${branchAddress?.contact.T}`}
                                                </>
                                            )}
                                            {branchAddress?.contact?.T && branchAddress?.contact?.M && <span> </span>}
                                            {branchAddress?.contact?.M && (
                                                <>
                                                    <span style={{ fontWeight: "600" }}> {`M`}</span> {`${branchAddress?.contact.M}`}
                                                </>
                                            )}
                                            {/* <span style={{ fontWeight: "600" }}> {`F`}</span> {`${branchAddress?.contact.F}`} */}
                                        </Box>
                                    )}
                                    {(branchAddress?.email || branchAddress?.website) && (
                                        <Box className="pdf-branch-phone" sx={{ fontWeight: "400", fontSize: "14px" }}>
                                            {branchAddress?.email && `${branchAddress?.email}`}
                                            {branchAddress?.email && branchAddress?.website && ' '}
                                            {branchAddress?.website && `${branchAddress?.website}`}
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ width: "20%", textAlign: "right", position: "relative" }}><img src={logo.logoInvoice} height={70} /></Box>
                            </Box> : <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: 0 }}>
                                <Box sx={{ width: "60%", textAlign: "center", marginTop: "20px" }}>
                                    <Box className="pdf-branch-name" sx={{ fontWeight: "500" }}>{BranchInfo?.branchName || ""} </Box>
                                    <Box className="pdf-branch-address">{BranchInfo?.receiptFullAddress || ""}</Box>
                                    <Box className="pdf-branch-phone">{BranchInfo?.branchPhone || ""}</Box>
                                </Box>
                            </Box>
                        }
                    </Box>
                    {/* <Modal
                        open={isOpenPageModal}
                        onClose={() => handleSignature("clear")}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            '.card-ap-detail': {
                                position: 'unset',
                                transform: 'none '
                            }
                        }}
                    ><><Box>{props.childrenModal}</Box></>
                    </Modal> */}

                    <PageModal
                        isOpen={isOpenPageModal}
                        size={'S'}
                        title={getTranslation('PDF_INVOICE.PDF.SIGNATURE')}
                        handleClose={() => handleSignature("clear")}
                        onSubmit={{ onClick: () => handleSignature("submit") }}
                        onCancel={{ onClick: () => handleSignature("clear") }}
                        overflow
                    >{props.childrenModal}</PageModal>
                </Box>
            </PDFContainer >
        </>
    )
}

export default PdfInvoice
